import { Accordion } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Button, Text } from "@nxt-costco-com/forge-components";
import { SpaceLg, ColorTextDefault, SpaceMd, SpaceXs, ColorGray900, ColorCostcoBlue, FontSize100, FontSize300, SpaceSm } from "@nxt-costco-com/forge-design-tokens";
import { ChevronDown } from "@nxt-costco-com/forge-icons/icons/ChevronDown";
import { ChevronUp } from "@nxt-costco-com/forge-icons/icons/ChevronUp";
import { MarketingTypography } from "../../MarketingTypography/index.js";
import { CostcoBox } from "../../../Util/CostcoBox.js";
import GenericImageHandler from "../../../Util/GenericImageHandler.js";
const MARKETING_TYPOGRAPHY_BODYCOPY = {
  fontSize: "1rem"
};
const getFlexSize = (sectionType) => {
  switch (sectionType) {
    case "full":
      return "100%";
    case "half":
      return "50%";
    case "one_third":
      return "33.33%";
    case "one_fourth":
      return "25%";
    default:
      return "";
  }
};
const getBackgroundValue = (backgroundGradientStyle, backgroundColor, firstBackgroundColor, secondBackgroundColor) => {
  if (backgroundGradientStyle === "solid") {
    return backgroundColor || "transparent";
  }
  if (backgroundGradientStyle === "linear") {
    return `linear-gradient(${firstBackgroundColor}, ${secondBackgroundColor})`;
  }
  if (backgroundGradientStyle === "radial") {
    return `radial-gradient(${firstBackgroundColor}, ${secondBackgroundColor})`;
  }
  return "transparent";
};
const getBorderRadius = (backgroundGradientStyle, isIconAsImage) => {
  const bottomBorderRadius = backgroundGradientStyle === "no-background" ? "0px" : "10px";
  const topBorderRadius = isIconAsImage ? "20px" : bottomBorderRadius;
  return `${topBorderRadius} ${topBorderRadius} ${bottomBorderRadius} ${bottomBorderRadius}`;
};
const COMMON_SX = {
  margin: "0px",
  width: "100%"
};
const CHEVRON_SX = {
  height: "20px",
  margin: "0px",
  padding: "0px",
  width: "20px"
};
const BUTTON_RESET_STYLES = {
  "&:active": {
    background: "none",
    backgroundColor: "none"
  },
  "&:focus": {
    background: "none",
    backgroundColor: "none"
  },
  "&:hover": {
    background: "none",
    backgroundColor: "none"
  },
  "&:visited": {
    background: "none",
    backgroundColor: "none"
  },
  background: "none",
  backgroundColor: "none"
};
const MainContainer = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "alignment" && prop !== "backgroundGradientStyle" && prop !== "backgroundColor" && prop !== "firstBackgroundColor" && prop !== "isIconAsImage" && prop !== "sectionType" && prop !== "secondBackgroundColor" && prop !== "textColor" && prop !== "hoverAsClickable" && prop !== "borderColor"
})(({
  alignment,
  backgroundGradientStyle,
  backgroundColor,
  firstBackgroundColor,
  isIconAsImage,
  sectionType,
  secondBackgroundColor,
  textColor,
  hoverAsClickable,
  borderColor
}) => {
  const flexSize = getFlexSize(sectionType);
  const backgroundValue = getBackgroundValue(
    backgroundGradientStyle,
    backgroundColor,
    firstBackgroundColor,
    secondBackgroundColor
  );
  const borderRadius = getBorderRadius(
    backgroundGradientStyle,
    isIconAsImage
  );
  return {
    MozBorderRadius: isIconAsImage ? "20px" : backgroundGradientStyle === "no-background" ? "0px" : "10px",
    WebkitBorderRadius: borderRadius,
    alignItems: alignment === "center" ? "center" : "stretch",
    background: backgroundValue,
    borderRadius,
    color: textColor || ColorTextDefault,
    display: "flex",
    flex: `1 1 calc(${flexSize} - ${SpaceLg})`,
    flexDirection: "column",
    flexWrap: "nowrap",
    cursor: hoverAsClickable ? "pointer" : "default",
    border: borderColor ? `1px solid ${borderColor}` : "none"
  };
});
const ContentContainer = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "alignment" && prop !== "backgroundGradientStyle" && prop !== "isIconAsImage" && prop !== "sectionType" && prop !== "textColor" && prop !== "isForgeIcon" && prop !== "isCustomParsingEnabled"
})(({
  alignment,
  backgroundGradientStyle,
  isIconAsImage,
  sectionType,
  textColor,
  theme,
  isForgeIcon,
  isCustomParsingEnabled = false
  // Default value added
}) => {
  const isAuto = sectionType === "auto";
  const isNoBackground = backgroundGradientStyle === "no-background";
  const isLeftAlign = alignment === "left";
  return {
    alignItems: alignment === "center" ? "center" : "stretch",
    background: "transparent",
    color: textColor || ColorTextDefault,
    display: "flex",
    flexDirection: alignment === "center" ? "column" : "row",
    flexWrap: "nowrap",
    gap: isForgeIcon ? SpaceXs : SpaceMd,
    [theme.breakpoints.up("xs")]: {
      padding: `${isAuto || isNoBackground ? "0px" : SpaceMd} ${isIconAsImage || !isNoBackground ? SpaceMd : "0px"}`
    },
    [theme.breakpoints.up("md")]: {
      padding: `${isAuto || isNoBackground ? "0px" : isLeftAlign ? SpaceLg : SpaceMd} 
            ${isLeftAlign ? !isNoBackground ? SpaceLg : "0px" : SpaceMd}`
    },
    [theme.breakpoints.up("lg")]: {
      padding: `${isAuto || isNoBackground ? "0px" : isIconAsImage ? SpaceMd : SpaceLg} ${isLeftAlign && isNoBackground ? "0px" : alignment === "center" ? SpaceMd : SpaceLg}`
    },
    ...isCustomParsingEnabled && {
      padding: "12px 12px !important",
      "& button": {
        color: "#333333"
      },
      a: {
        color: "#333333 !important"
      }
    }
  };
});
const HeaderImage = styled("img")({
  borderRadius: "10px 10px 0px 0px",
  height: "auto",
  width: "100%"
});
const IconImgBox = styled(GenericImageHandler, {
  shouldForwardProp: (prop) => prop !== "alignment" && prop !== "imageSize"
})(({ alignment, theme, imageSize }) => ({
  maxHeight: alignment ? "auto" : "100%",
  [theme.breakpoints.up("xs")]: {
    height: `${imageSize || 56}px`,
    objectFit: "contain",
    width: `${imageSize || 56}px`
  },
  [theme.breakpoints.up("md")]: {
    height: `${imageSize || 80}px`,
    objectFit: "contain",
    width: `${imageSize || 80}px`
  }
}));
const Content = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "alignment"
})(({ alignment }) => ({
  alignItems: alignment === "center" ? "center" : "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: SpaceXs
}));
const Title = styled(MarketingTypography, {
  shouldForwardProp: (prop) => prop !== "alignment" && prop !== "color"
})(({ alignment, color }) => ({
  ...COMMON_SX,
  color: color || ColorGray900,
  textAlign: alignment
}));
const Subtitle = styled(MarketingTypography, {
  shouldForwardProp: (prop) => prop !== "alignment" && prop !== "color"
})(({ alignment, color }) => ({
  ...COMMON_SX,
  color: color || ColorGray900,
  textAlign: alignment
}));
const BodyText = styled(CostcoBox, {
  shouldForwardProp: (prop) => prop !== "lines"
})(({ lines }) => ({
  "&.body-text-ellipsis": {
    WebkitBoxOrient: "vertical",
    lineClamp: `${lines}`,
    overflow: "hidden",
    textOverflow: "ellipsis",
    textWrap: "wrap",
    display: "-webkit-box",
    WebkitLineClamp: `${lines}`
  },
  display: "inline-block",
  flexDirection: "column",
  gap: SpaceXs
}));
const Body = styled(MarketingTypography, {
  shouldForwardProp: (prop) => prop !== "alignment" && prop !== "color" && prop !== "customLinkColor"
})(({ color, alignment, customLinkColor }) => ({
  a: {
    color: customLinkColor || ColorCostcoBlue,
    textDecoration: "underline"
  },
  button: {
    textDecoration: "underline"
  },
  color: color || ColorGray900,
  maxWidth: "100%",
  ul: {
    marginTop: "0px",
    marginBottom: "0px"
  },
  textAlign: alignment,
  width: "100%",
  "p:not(:last-child)": {
    marginTop: "0px",
    marginBottom: "1rem"
  },
  "p:last-child": {
    marginTop: "0px",
    marginBottom: "0px"
  }
}));
const Disclaimer = styled(MarketingTypography, {
  shouldForwardProp: (prop) => prop !== "color"
})(({ alignment, color }) => ({
  ...COMMON_SX,
  "&.textbuildercard-ui-disclaimer-hidden": {
    display: "none"
  },
  color: color || ColorGray900,
  textAlign: alignment,
  p: {
    margin: "0px"
  }
}));
const IconSx = {
  height: {
    md: "80px",
    xs: "56px"
  },
  paddingTop: SpaceSm,
  width: {
    md: "80px",
    xs: "56px"
  }
};
const SeeMoreAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== "alignment"
})(({ alignment }) => ({
  "& .MuiButtonBase-root": {
    ".MuiAccordionSummary-content": {
      margin: "0px"
    },
    display: "flex",
    gap: SpaceXs,
    justifyContent: "space-between",
    margin: "0px",
    minHeight: "auto",
    padding: "0px"
  },
  "& .MuiButtonBase-root.Mui-expanded": {
    minHeight: "0px"
  },
  "&::before": {
    display: "none"
  },
  ".Mui-expanded": {
    marginTop: "0px !important"
  },
  ".MuiAccordionDetails-root": {
    padding: "0px"
  },
  alignItems: alignment === "center" ? "center" : "flex-start",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  fontSize: FontSize100,
  lineHeight: "24px",
  margin: "0px !important",
  padding: "0px !important",
  width: "100%"
}));
const ShowHideButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "alignment"
})(({ alignment }) => ({
  ...BUTTON_RESET_STYLES,
  display: "flex",
  gap: SpaceXs,
  height: "auto",
  justifyContent: alignment === "center" ? "center" : "flex-start",
  padding: "0px",
  width: "auto"
}));
const AccordionTitle = styled(Text, {
  shouldForwardProp: (prop) => prop !== "color"
})(({ color }) => ({
  color: color || ColorTextDefault,
  fontSize: FontSize300,
  lineHeight: "24px"
}));
const StyledChevronDown = styled(ChevronDown, {
  shouldForwardProp: (prop) => prop !== "color"
})(({ color }) => ({
  ...CHEVRON_SX,
  color: color || ColorTextDefault
}));
const StyledChevronUp = styled(ChevronUp, {
  shouldForwardProp: (prop) => prop !== "color"
})(({ color }) => ({
  ...CHEVRON_SX,
  color: color || ColorTextDefault
}));
const MarketingTypographyStyled = styled(MarketingTypography, {
  shouldForwardProp: (prop) => prop !== "color"
})(({ color }) => ({
  color: color || ColorGray900
}));
const marketingTypographyBodycopy = MARKETING_TYPOGRAPHY_BODYCOPY;
export {
  AccordionTitle,
  Body,
  BodyText,
  Content,
  ContentContainer,
  Disclaimer,
  HeaderImage,
  IconImgBox,
  IconSx,
  MainContainer,
  MarketingTypographyStyled,
  SeeMoreAccordion,
  ShowHideButton,
  StyledChevronDown,
  StyledChevronUp,
  Subtitle,
  Title,
  marketingTypographyBodycopy
};
